import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Dropdown, Modal, Button, Form } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import { Chart, registerables, Tooltip } from "chart.js";
import "chart.js/auto";
import "react-multi-carousel/lib/styles.css";
import "bootstrap/dist/css/bootstrap.min.css";
import Cookies from "js-cookie";
import { RotatingLines } from "react-loader-spinner";
import "chartjs-plugin-dragdata";
import * as XLSX from "xlsx";

Chart.register(...registerables);

const API_URL = `https://options.healthsaving.life/api/`;

// Fetch acct_names from the API
const fetchacct_names = async () => {
  let api_url = `${API_URL}get_option_chain_ibkr_positions_acct_name.php`;

  const response = await fetch(`${api_url}`);

  const data = await response.json();
  return data.data.map((item) => item.acct_name);
};

// Fetch symbols from the API
const fetchSymbols = async (portfolioMode, selectedacct_name) => {
  let api_url = `${API_URL}get_option_chain_ibkr_positions_symbol.php`;

  if (portfolioMode) {
    api_url = `${API_URL}get_option_chain_ibkr_positions_symbol.php?acct_name=${selectedacct_name}`;
  }

  const response = await fetch(`${api_url}`);

  const data = await response.json();
  return data.data.map((item) => item.symbol);
};

// Fetch expiry dates based on the selected symbol
const fetchExpiries = async (symbol, portfolioMode, selectedacct_name) => {
  if (symbol) {
    let api_url = `${API_URL}get_option_chain_ibkr_positions_expiry.php?symbol=${symbol}`;
    if (portfolioMode) {
      api_url = `${API_URL}get_option_chain_ibkr_positions_expiry.php?symbol=${symbol}&acct_name=${selectedacct_name}`;
    }

    const response = await fetch(`${api_url}`);
    const data = await response.json();
    if (data) {
      return data;
      // return data.data.map(item => item.expiry);
    } else {
      return [];
    }
  } else {
    return [];
  }
};

// Fetch expiry dates based on the selected symbol
const fetchStrikePriceRange = async (
  symbol,
  expiry,
  portfolioMode,
  selectedacct_name
) => {
  if (symbol) {
    let api_url = `${API_URL}get_option_chain_ibkr_positions_data.php?symbol=${symbol}`;

    if (portfolioMode) {
      api_url = `${API_URL}get_option_chain_ibkr_positions_data.php?symbol=${symbol}&acct_name=${selectedacct_name}`;

      if (expiry) {
        api_url = `${API_URL}get_option_chain_ibkr_positions_data.php?symbol=${symbol}&expiry=${expiry}&acct_name=${selectedacct_name}`;
      }
    }

    const response = await fetch(`${api_url}`);
    const data = await response.json();
    return data.data;
  } else {
    return [];
  }
};

// Fetch portfolio data from the API
const fetchPortfolio = async (symbol, expiry, selectedacct_name) => {
  // console.log('symbol',symbol);
  if (symbol) {
    const response = await fetch(
      `${API_URL}get_option_chain_ibkr_positions.php?symbol=${symbol}&expiry=${expiry}&acct_name=${selectedacct_name}`
    );
    const data = await response.json();
    // console.log('fetch get_option_chain_ibkr_positions:', data);
    if (data.result) {
      return data.data[0];
    } else {
      return {
        symbol: "",
        expiry: "",
        strikes: '[{"type":"Buy","values":[]},{"type":"Sell","values":[]}]',
        underlying_data: [],
        avg_price_data: [],
      };
    }
  } else {
    return {
      symbol: "",
      expiry: "",
      strikes: '[{"type":"Buy","values":[]},{"type":"Sell","values":[]}]',
      underlying_data: [],
      avg_price_data: [],
    };
  }
};


// Helper function to calculate the cumulative normal distribution function (CDF)
const cumulativeNormalDistribution = (x) => {
  const p = 0.2316419;
  const b1 = 0.31938153;
  const b2 = -0.356563782;
  const b3 = 1.781477937;
  const b4 = -1.821255978;
  const b5 = 1.330274429;

  // Handle the symmetry for negative values of x
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);

  const t = 1 / (1 + p * x);
  const poly = ((((b5 * t + b4) * t + b3) * t + b2) * t + b1) * t;
  const d = 1 - (Math.exp((-x * x) / 2) / Math.sqrt(2 * Math.PI)) * poly;

  return sign === 1 ? d : 1 - d;
};

// Black-Scholes pricing function
const calculateBlackScholes = (S, K, T, r, sigma, type) => {
  const d1 =
    (Math.log(S / K) + (r + sigma ** 2 / 2) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);

  const callPrice =
    S * cumulativeNormalDistribution(d1) -
    K * Math.exp(-r * T) * cumulativeNormalDistribution(d2);
  const putPrice =
    K * Math.exp(-r * T) * cumulativeNormalDistribution(-d2) -
    S * cumulativeNormalDistribution(-d1);

  // Check if any calculation results in NaN and return 0 if so
  if (isNaN(callPrice) || isNaN(putPrice)) {
    return 0;
  }

  return type === "call" ? callPrice : putPrice;
};

// Main component for options graph and multi-strike slider
const OptionsGraphForecast = () => {
  const checkFirstRender1 = useRef(true);
  const checkFirstRender2 = useRef(true);

  const navigate = useNavigate(); // For URL manipulation
  const location = useLocation(); // To access current URL

  const [symbols, setSymbols] = useState([]);
  const [expiries, setExpiries] = useState([]);
  const [allExpiries, setAllExpiries] = useState([]);
  const [maxExpiry, setMaxExpiry] = useState(null);
  const [strikes, setStrikes] = useState([]);
  const [strikesBeta, setStrikesBeta] = useState([]);
  const [strikesChange, setStrikesChange] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [acct_names, setacct_names] = useState(["Walleyhouse"]);
  const [selectedacct_name, setSelectedacct_name] = useState("Walleyhouse");
  const [selectedExpiry, setSelectedExpiry] = useState([]);
  const [minStrikePrice, setMinStrikePrice] = useState(0);
  const [maxStrikePrice, setMaxStrikePrice] = useState(0);
  const [strike_range, setstrike_range] = useState(0);
  const [stockPrice, setStockPrice] = useState(0);
  const [range, setRange] = useState([0, 100]);
  const [sliderRange, setSliderRange] = useState(1);
  const [date, setDate] = useState(0);

  const [currentGroupIndex, setCurrentGroupIndex] = useState(null);
  const [currentValueIndex, setCurrentValueIndex] = useState(null);

  const [bidprice, setBidPrice] = useState(0);
  const [askprice, setAskPrice] = useState(0);
  const [volume, setVolume] = useState(0);
  const [oi, setOI] = useState(0);
  const [iv, setIV] = useState(0);
  const [delta, setDelta] = useState(0);
  const [theta, setTheta] = useState(0);
  const [gamma, setGamma] = useState(0);
  const [vega, setVega] = useState(0);
  const [rho, setRho] = useState(0);

  const [portfolioMode, setPortfolioMode] = useState(true); // Portfolio or Custom mode
  const [contractSize, setContractSize] = useState(100);
  const [hideACCName, setHideACCName] = useState("block");

  // const [screenWidth, setScreenWidth] = useState(2000);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [min, setMin] = useState(0); // Minimum value
  const [max, setMax] = useState(0); // Maximum value
  const sliderWidth = screenWidth; // Set a fixed width for the slider container

  const [avgPriceData, setavgPriceData] = useState([]);

  const [loading, setLoading] = useState(false);

  const [thumbMoved, setThumbMoved] = useState(false);

  // State for modal visibility, form inputs, and error message
  const [show, setShow] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");

  const [sliderOverPad, setsliderOverPad] = useState(40);

  const [isLocked, setIsLocked] = useState(true);

// Predefined credentials
const predefinedCredentials = [
  { username: 'admin', password: 'Me6OngDR4C' },
  { username: 'user@gmail.com', password: '123456' }
];

// Check for stored credentials in cookies when the component mounts
useEffect(() => {
  const storedUsername = Cookies.get('username');
  const storedPassword = Cookies.get('password');

  const isValid = predefinedCredentials.some(
    cred => cred.username === storedUsername && cred.password === storedPassword
  );

  if (isValid) {
    setShow(false);
  } else {
    setShow(true); // Show modal if credentials are not found or do not match
  }
}, []);

// Handle login form submission
const handleLogin = (e) => {
  e.preventDefault();
  const isValid = predefinedCredentials.some(
    cred => cred.username === username && cred.password === password
  );

  if (isValid) {
    setError('');
    setShow(false); // Close modal on successful login

    // Store credentials in cookies based on "Remember Me" checkbox
    if (rememberMe) {
      Cookies.set('username', username, { expires: 365 });
      Cookies.set('password', password, { expires: 365 });
    } else {
      Cookies.set('username', username);
      Cookies.set('password', password);
    }
  } else {
    setError('Invalid username or password'); // Show error, modal stays open
  }
};

  // console.log('screenWidth',screenWidth);
  // console.log('min',min);
  // console.log('max',max);
  // console.log('range',range);
  // console.log('strike_range',strike_range);
  // console.log('minStrikePrice',minStrikePrice);
  // console.log('maxStrikePrice',maxStrikePrice);
  // console.log('hideExpiry',hideExpiry);
  // console.log('selectedExpiry',selectedExpiry);
  // console.log('strikes',strikes);
  // console.log('strikesChange',strikesChange);
  // console.log('symbols',symbols);
  // console.log('selectedSymbol',selectedSymbol);
  // console.log('allExpiries',allExpiries);
  // console.log('maxExpiry',maxExpiry);
  // console.log('underlyingData',underlyingData);
  // console.log('expiries',expiries);
  // console.log('stockPrice',stockPrice);
  // console.log('avgPriceData',avgPriceData);
  // console.log('optionsStrikes',optionsStrikes);

  // const fetchOptionsStrikes = async (expiry, selectedacct_name) => {
  //   const response = await fetch(
  //     `${API_URL}get_gamma_option_chain_strike.php?symbol=${selectedSymbol}&expiry=${expiry}&acct_name=${selectedacct_name}`
  //   );
  //   const data = await response.json();
  // };

  // Helper function to update the URL
  const updateUrlParams = (params) => {
    // console.log('params:',params);
    if (portfolioMode) {
      // savePortfolioData();
    } else {
      const searchParams = new URLSearchParams(location.search);
      Object.keys(params).forEach((key) => {
        if (params[key]) {
          searchParams.set(key, params[key]);
        } else {
          searchParams.delete(key);
        }
      });
      navigate({ search: searchParams.toString() });
    }
  };

  const handleacct_nameChange = (acct_name) => {
    setSelectedacct_name(acct_name);
    setSelectedSymbol("");
    setSelectedExpiry("");
    setSymbols([]);
  };

  useEffect(() => {
    // console.log('useeffect fetch acct_namesList render');
    const loadacct_names = async () => {
      const acct_namesList = await fetchacct_names();
      // setLoading(true);
      setacct_names(acct_namesList);
      setSelectedacct_name(acct_namesList[0]);
      // setSelectedSymbol('');
      setSelectedExpiry([]);
    };
    loadacct_names();
  }, []);

  // When symbol or expiry changes, save or update based on mode
  useEffect(() => {
    // console.log('useeffect fetch loadSymbols render');

    const loadSymbols = async () => {
      const symbolList = await fetchSymbols(portfolioMode, selectedacct_name);
      setSymbols(symbolList);
      setSelectedSymbol(symbolList[0]);

      const searchParams = new URLSearchParams(location.search);
      const symbolFromUrl = searchParams.get("symbol");
      const expiryFromUrl = searchParams.get("expiry");
      const modeFromUrl = searchParams.get("mode");
      const underlyingDataURL = searchParams.get("underlyingData");
      const strikesFromUrl = searchParams.get("strikes");

      if (modeFromUrl) {
        setPortfolioMode(false);
      }

      if (!portfolioMode) {
        if (symbolFromUrl) {
          setSelectedSymbol(symbolFromUrl); //symbol load from URL

          if (strikesFromUrl) {
            // console.log('8 search url',JSON.parse(strikesFromUrl));

            setStrikes(JSON.parse(strikesFromUrl)); //strikes load from url
            setStrikesChange(JSON.parse(strikesFromUrl)); //strikes load from url
          } else {
            setStrikes([
              { type: "Buy", values: [] },
              { type: "Sell", values: [] },
            ]);
            setStrikesChange([
              { type: "Buy", values: [] },
              { type: "Sell", values: [] },
            ]);
          }
        }

        // console.log('already set strikes',strikes);
      } else {
        setSelectedExpiry([]);
      }

      setPopupData(null);
    };
    loadSymbols();
  }, [location.search, portfolioMode, selectedacct_name]);

  useEffect(() => {
    // console.log('useeffect fetch loadExpiries render');
    const loadExpiries = async () => {
      if (selectedSymbol) {
        const expiryList = await fetchExpiries(
          selectedSymbol,
          portfolioMode,
          selectedacct_name
        );
        // console.log('expiryList:',expiryList);

        const expiryListdata = Array.isArray(expiryList?.data)
          ? expiryList.data.map((item) => item.expiry)
          : []; // Default to an empty array if data is null or not an array

        const allExpiryListdata = Array.isArray(expiryList?.all_expiry)
          ? expiryList.all_expiry.map((item) => item.expiry)
          : []; // Default to an empty array if all_expiry is null or not an array

        // console.log('expiryListdata:',expiryListdata);

        if (portfolioMode) {
          setExpiries(expiryListdata);
          const maxDate = new Date(
            Math.max(...allExpiryListdata.map((date) => new Date(date)))
          );
          setMaxExpiry(maxDate); // Format as "YYYY-MM-DD"
          // setImpliedVolatility(expiryList.iv);
        }

        setAllExpiries(allExpiryListdata);
      }
    };
    loadExpiries();
  }, [selectedSymbol, portfolioMode]);

  useEffect(() => {
    // console.log('useeffect fetch fetchStrikeData render');
    const fetchStrikeData = async (
      symbol,
      expiry,
      portfolioMode,
      selectedacct_name
    ) => {
      try {
        if (symbol) {
          const response = await fetchStrikePriceRange(
            symbol,
            expiry,
            portfolioMode,
            selectedacct_name
          ); // Ensure this function is awaited

          if (response.length > 0) {
            const { min_strike, max_strike, cmp, strike_range } = response[0];

            setMinStrikePrice(min_strike);
            setMaxStrikePrice(max_strike);

            setStockPrice(cmp);

            // console.log('stockPrice,min_strike,max_strike',stockPriceCMP,min_strike,max_strike);

            setstrike_range(strike_range);

            setMin(min_strike);
            setMax(max_strike);

            setRange([min_strike, max_strike]);
          } else {
            console.error("Error: No strike data available");
          }
        }
      } catch (error) {
        console.error("Error fetching strike data:", error);
      }
    };
    fetchStrikeData(
      selectedSymbol,
      selectedExpiry,
      portfolioMode,
      selectedacct_name
    );
  }, [selectedSymbol, portfolioMode, selectedacct_name]);

  useEffect(() => {
    //can skip first render

    // console.log('useeffect updateSlider render');

    if (checkFirstRender2.current) {
      checkFirstRender2.current = false;
      return;
    }

    updateSlider();
  }, [strikes, min, max]);

  useEffect(() => {
    // console.log('useeffect fetch loadPortfolioData render');
    if (portfolioMode) {
      const loadPortfolioData = async () => {
        let selectedSymbolData = symbols[0];

        if (selectedSymbol) {
          selectedSymbolData = selectedSymbol;
        }

        if (selectedSymbolData) {
          const portfolioData = await fetchPortfolio(
            selectedSymbolData,
            selectedExpiry,
            selectedacct_name
          );

          // console.log('selectedSymbolData:',selectedSymbolData,selectedSymbol,symbols[0]);
          // console.log('portfolioData:',portfolioData);
          setLoading(false);

          setStrikes(JSON.parse(portfolioData.strikes)); //strikes added
          setStrikesBeta(JSON.parse(portfolioData.strikesBeta)); //strikes added
          setStrikesChange(JSON.parse(portfolioData.strikes));

          setPopupData(null);

          if (portfolioData.avg_price_data) {
            setavgPriceData(portfolioData.avg_price_data);
          }
        }
      };
      loadPortfolioData();
    }
  }, [selectedacct_name, symbols, selectedSymbol, portfolioMode]);

  const handleSymbolChange = (symbol) => {
    if (portfolioMode) {
      setTimeout(() => {
        setLoading(true); // Show loader
      }, 600); // Adjust timeout to match your actual load time
    }

    setSelectedExpiry([]);
    setSelectedSymbol(symbol);
    setSliderRange(1);

    updateUrlParams({
      mode: "custom",
      symbol: symbol,
      expiry: "",
      strikes: "",
    });

    setTimeout(() => {
      setLoading(false); // Hide loader once done
    }, 5000); // Adjust timeout to match your actual load time

    setThumbMoved(false);

    // console.log('textContentRefLeft:',textContentRefLeft);

    // Reset all checkboxes to checked
    setCheckedItems(
      filterDropdownItems.reduce((acc, _, idx) => {
        acc[idx] = true; // Set all items to checked
        return acc;
      }, {})
    );
  };

  // Function to update expiry in popupData and values array
  const handleExpiryChangePopup = (newExpiry) => {
    if (popupData && currentGroupIndex !== null && currentValueIndex !== null) {
      // Update the popup data's expiry
      setPopupData((prevData) => ({
        ...prevData,
        expiry: newExpiry,
      }));

      // console.log('newExpiry:',newExpiry,popupData.value);

      // Find matching entry in avgPriceData with consistent types
      const matchingData = avgPriceData.find(
        (item) =>
          item.expiry === newExpiry &&
          parseFloat(item.value) === parseFloat(popupData.value) &&
          item.stype === popupData.stype
      );

      // Set avg_price_share from matchingData, if it exists; otherwise default to an empty string
      const avgPriceShare = matchingData ? matchingData.avg_price_share : 0;

      // console.log('matchingData:',matchingData,newExpiry,popupData.value,popupData.stype,avgPriceShare);

      // Update the values array with the new expiry
      const updatedValues = [...strikes];
      updatedValues[currentGroupIndex].values[currentValueIndex].expiry =
        newExpiry;
      updatedValues[currentGroupIndex].values[
        currentValueIndex
      ].avg_price_share = avgPriceShare;
      updatedValues[currentGroupIndex].values[
        currentValueIndex
      ].avg_price_share_new = avgPriceShare;

      if (!expiries.includes(newExpiry)) {
        expiries.push(newExpiry);
      }

      if (selectedExpiry.length != 0) {
        if (!selectedExpiry.includes(newExpiry)) {
          selectedExpiry.push(newExpiry);
          updateStrikes(selectedSymbol, selectedExpiry); // Update strikes when expiries change
        }
      }

      setSelectedExpiry(selectedExpiry);

      setStrikes(updatedValues); // If necessary to update strikes
      //setStrikesChange(updatedValues); // If necessary to update strikes
      // console.log('expiries change updatedValues:',updatedValues);

      updateUrlParams({
        mode: "custom",
        symbol: selectedSymbol,
        strikes: JSON.stringify(updatedValues),
      });
      // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: selectedExpiry, strikes: JSON.stringify(updatedValues) });
    }
  };

  const updateStrikes = (symbol, expiry) => {
    // console.log('updateStrikes - Selected expiry:', expiry);

    // Filter strikes based on expiry if provided
    const filteredStrikes = strikesChange.map((strike) => {
      // If expiry array is empty, include all values
      if (expiry.length === 0) {
        return strike;
      }

      // Filter values within each strike based on expiry
      const filteredValues = strike.values.filter((value) =>
        expiry.includes(value.expiry)
      );

      return {
        ...strike,
        values: filteredValues,
      };
    });

    // Find min and max strike values after filtering
    let minStrike = Infinity;
    let maxStrike = -Infinity;

    filteredStrikes.forEach((strike) => {
      strike.values.forEach((value) => {
        if (value.value < minStrike) {
          minStrike = value.value;
        }
        if (value.value > maxStrike) {
          maxStrike = value.value;
        }
      });
    });

    // Handle the case where no values are present after filtering
    if (minStrike === Infinity) minStrike = 1;
    if (maxStrike === -Infinity) maxStrike = 0;

    // Log or set the min and max strike values
    // console.log('Min Strike:', minStrike, 'Max Strike:', maxStrike);

    if (minStrike == null) {
      minStrike = 1;
    }

    if (maxStrike == null) {
      maxStrike = 1;
    }

    const minStrike_range = minStrike - minStrike * 0.05;
    const maxStrike_range = maxStrike + maxStrike * 0.05;

    // console.log('filteredStrikes:', filteredStrikes);
    // Set filtered strikes
    setStrikes(filteredStrikes);
    // setStrikesChange(filteredStrikes);

    updateUrlParams({
      mode: "custom",
      symbol: selectedSymbol,
      strikes: JSON.stringify(filteredStrikes),
    });
    // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: selectedExpiry, strikes: JSON.stringify(filteredStrikes) });

    setPopupData(null);
  };

  const dropdownItems = [
    { type: "Buy Call", label: "Buy", labelType: "Call" },
    { type: "Sell Call", label: "Sell", labelType: "Call" },
    { type: "Buy Put", label: "Buy", labelType: "Put" },
    { type: "Sell Put", label: "Sell", labelType: "Put" },
  ];

  const filterDropdownItems = [
    { type: "Buy Call", label: "Long", labelType: "Call" },
    { type: "Sell Call", label: "Short", labelType: "Call" },
    { type: "Buy Put", label: "Long", labelType: "Put" },
    { type: "Sell Put", label: "Short", labelType: "Put" },
  ];

  const [checkedItems, setCheckedItems] = useState(
    filterDropdownItems.reduce((acc, _, idx) => {
      acc[idx] = true; // All items are checked by default
      return acc;
    }, {})
  );

  const dropdownItems_underlying = [{ type: "Buy" }, { type: "Sell" }];

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
      slidesToSlide: 3, // optional, default to 1.
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2,
      slidesToSlide: 2, // optional, default to 1.
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
      slidesToSlide: 1, // optional, default to 1.
    },
  };

  // custom-slider

  const [popupData, setPopupData] = useState(""); // For storing popup information
  const sliderRef = useRef();

  // Assuming the first element in strike_data is what you are working with

  const strikeRange = Array.isArray(strike_range) ? strike_range : [];

  const validStrikes = strikeRange
    .map((s) => s.strike)
    .filter((s) => s >= minStrikePrice && s <= maxStrikePrice); // Valid strikes

  // console.log('validStrikes:', validStrikes);

  // Function to move a thumb based on mouse movement (updated to snap to closest valid strike)
  const moveThumb = (event, groupIndex, valueIndex) => {
    const rect = sliderRef.current.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;

    // Calculate the value proportionally
    const rawValue = (offsetX / sliderWidth) * (max - min) + min;

    // Snap to the closest valid strike
    const closestStrike = validStrikes.reduce((prev, curr) =>
      Math.abs(curr - rawValue) < Math.abs(prev - rawValue) ? curr : prev
    );

    if (closestStrike < minStrikePrice || closestStrike > maxStrikePrice)
      return;

    // Update the thumb's position and associated data
    const newValues = [...strikes];
    const matchingData = avgPriceData.find(
      (item) =>
        item.expiry === newValues[groupIndex].values[valueIndex].expiry &&
        item.value === closestStrike &&
        item.stype === newValues[groupIndex].values[valueIndex].stype
    );

    const avgPriceShare = matchingData ? matchingData.avg_price_share : 0;

    newValues[groupIndex].values[valueIndex].value = closestStrike;
    newValues[groupIndex].values[valueIndex].avg_price_share = avgPriceShare;
    newValues[groupIndex].values[valueIndex].avg_price_share_new =
      avgPriceShare;

    setStrikes(newValues);
    updateSlider(); // Reflect changes in the slider UI
  };

  const updateSlider = () => {
    const thumbPositions = {}; // Track positions of thumbs for stacking
    const stackLevels = {}; // Track stack levels for each thumb group
    let maxStackLevel = 0; // Variable to store the maximum stack level
    const thumbCount = { "above-slider": 0, "below-slider": 0 }; // Counter for thumbs in each group

    if (Array.isArray(strikes)) {
      strikes.forEach((group, groupIndex) => {
        // Ensure group.values is an array before iterating
        if (Array.isArray(group.values)) {
          group.values.forEach((item, valueIndex) => {
            if (validStrikes.includes(item.value)) {
              const percent = ((item.value - min) / (max - min)) * 100;
              const thumb = sliderRef.current.querySelector(
                `.thumb-${groupIndex}-${valueIndex}`
              );

              if (thumb) {
                // Set the horizontal position
                thumb.style.left = `${percent}%`;

                // Determine stacking for overlapping thumbs
                const thumbGroup =
                  group.type === "Buy" ? "above-slider" : "below-slider";
                thumbPositions[thumbGroup] = thumbPositions[thumbGroup] || [];
                stackLevels[thumbGroup] = stackLevels[thumbGroup] || {};

                const thumbWidth = 18; // CSS width of thumbs
                const sliderWidth = sliderRef.current?.offsetWidth || 1; // Safe slider width
                const thumbWidthPercent = (thumbWidth / sliderWidth) * 100; // Thumb width in %

                // Initialize overlapThumbs as an empty array for each thumb
                let overlapThumbs = [];

                // Check for overlapping thumbs by comparing with other thumbs in the group
                thumbPositions[thumbGroup].forEach((pos) => {
                  if (Math.abs(pos - percent) < thumbWidthPercent) {
                    overlapThumbs.push(pos);
                  }
                });

                let stackLevel;
                if (overlapThumbs.length > 0) {
                  // If overlapping, use the highest stack level of overlapping thumbs
                  stackLevel =
                    Math.max(
                      ...overlapThumbs.map(
                        (pos) => stackLevels[thumbGroup][pos] || 0
                      )
                    ) + 1;
                } else {
                  // No overlap with the next thumb, reset stack level to 0
                  stackLevel = 0;
                }

                // Store the stack level for this position
                stackLevels[thumbGroup][percent] = stackLevel;

                // Update the maximum stack level
                maxStackLevel = Math.max(maxStackLevel, stackLevel);

                // Update the number of thumbs in the group
                thumbCount[thumbGroup] += 1;

                if (thumbGroup === "above-slider") {
                  // Vertical stacking for above-slider
                  thumb.style.top = `${-35 * stackLevel}px`; // Adjust height as needed
                  thumb.style.bottom = ""; // Reset bottom to avoid conflicts
                } else {
                  // Vertical stacking for below-slider
                  thumb.style.top = `${35 * stackLevel}px`; // Adjust height as needed
                  thumb.style.bottom = ""; // Reset bottom to avoid conflicts
                }

                // Track the thumb position
                thumbPositions[thumbGroup].push(percent);
              }
            }
          });
        }
      });
    } else {
      console.error("strikes is not an array:", strikes);
    }

    if (maxStackLevel > 0) {
      const sliderOverPadVal = 40 + maxStackLevel * 27;
      setsliderOverPad(sliderOverPadVal);
    } else {
      setsliderOverPad(40);
    }

    // console.log(`Maximum Stack Level: ${maxStackLevel}`);
    // console.log(`Number of Thumbs Above Slider: ${thumbCount["above-slider"]}`);
    // console.log(`Number of Thumbs Below Slider: ${thumbCount["below-slider"]}`);

    const totalThumbs = thumbCount["above-slider"] + thumbCount["below-slider"];

    if (totalThumbs > 10) {
      setScreenWidth(2000);
    } else {
      setScreenWidth(window.innerWidth);
    }
  };

  // Mouse down event handler for dragging thumbs
  const handleMouseDown = (groupIndex, valueIndex) => (event) => {
    if (isLocked) return;

    event.preventDefault(); // Prevent default browser behavior
    event.stopPropagation(); // Stop the event from propagating to other handlers

    setIsThumbDragging(true); // Indicate thumb dragging started

    const moveHandler = (e) => moveThumb(e, groupIndex, valueIndex);
    const upHandler = () => {
      setIsThumbDragging(false); // Indicate thumb dragging stopped
      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("mouseup", upHandler);
    };

    window.addEventListener("mousemove", moveHandler);
    window.addEventListener("mouseup", upHandler);
  };

  // Right-click event to show the popup
  const handleContextMenucus = (event, groupIndex, valueIndex) => {
    event.preventDefault(); // Prevent the browser's default right-click menu
    const rect = sliderRef.current.getBoundingClientRect();

    // Get thumb's details (value, quantity, stype) and set the popup position
    const thumbData = strikes[groupIndex].values[valueIndex];

    // Calculate the thumb's current `left` percentage based on its value
    const thumbLeftPercentage = ((thumbData.value - min) / (max - min)) * 100;

    setPopupData({
      ...thumbData,
      x:
        rect.left + (thumbLeftPercentage / 100) * sliderRef.current.offsetWidth, // Adjusted to include thumb position
      y: rect.top,
    });

    const newValues = [...strikes];

    // console.log('thumbData:',thumbData,rect);

    // Find matching entry in avgPriceData based on expiry, value, and stype
    const matchingData = avgPriceData.find(
      (item) =>
        item.expiry === thumbData.expiry &&
        item.value === thumbData.value &&
        item.stype === thumbData.stype
    );

    const bid = matchingData ? matchingData.bid : 0;
    const ask = matchingData ? matchingData.ask : 0;
    const iv = matchingData ? matchingData.iv * 100 : 0;
    const open_interest = matchingData ? matchingData.open_interest : 0;
    const volume = matchingData ? matchingData.volume : 0;
    const delta = matchingData ? matchingData.delta : 0;
    const gamma = matchingData ? matchingData.gamma : 0;
    const theta = matchingData ? matchingData.theta : 0;
    const rho = matchingData ? matchingData.rho : 0;
    const vega = matchingData ? matchingData.vega : 0;

    setBidPrice(bid);
    setAskPrice(ask);
    setVolume(volume);
    setOI(open_interest);
    setIV(iv);
    setDelta(delta);
    setTheta(theta);
    setGamma(gamma);
    setVega(vega);
    setRho(rho);

    // Set the current group and value indices
    setCurrentGroupIndex(groupIndex);
    setCurrentValueIndex(valueIndex);
  };

  // Close the popup when clicking elsewhere
  const closePopup = () => {
    setPopupData(null);
  };

  useEffect(() => {
    // Get current date and max date
    const currentDate = new Date();
    const maxDate = new Date(maxExpiry);

    const date1Ms = currentDate.getTime();
    const date2Ms = maxDate.getTime();

    const differenceMs = date2Ms - date1Ms;
    const differenceDaysSlider = Math.floor(
      differenceMs / (1000 * 60 * 60 * 24)
    );

    if (differenceDaysSlider > 0) {
      // console.log('differenceDaysSlider:',differenceDaysSlider);
      maxDate.setDate(currentDate.getDate() + differenceDaysSlider);
    } else {
      maxDate.setDate(currentDate.getDate() + 30);
    }
  }, [allExpiries]);

  useEffect(() => {
    //can skip first render

    // console.log('useeffect set sliderRange render');
    // console.log('sliderRange',sliderRange);

    if (checkFirstRender1.current) {
      checkFirstRender1.current = false;
      return;
    }

    const range0 = range[0] - range[0] * sliderRange * 0.01;
    const range1 = range[1] + range[1] * sliderRange * 0.01;

    setMinStrikePrice(range0);
    setMaxStrikePrice(range1);

    setMin(range0);
    setMax(range1);
  }, [sliderRange]);

  const handleQuantityChangepop = (change) => {
    if (popupData) {
      const updatedQuantity = popupData.quantity + change;
      if (updatedQuantity < 0) return; // Prevent negative quantities

      // Update the popupData state
      setPopupData((prevData) => ({
        ...prevData,
        quantity: updatedQuantity,
      }));

      // Update the values array
      const updatedValues = [...strikes];
      const groupIndex = strikes.findIndex((group) =>
        group.values.some(
          (value) =>
            value.value === popupData.value && value.stype === popupData.stype
        )
      );

      const valueIndex = strikes[groupIndex].values.findIndex(
        (value) =>
          value.value === popupData.value && value.stype === popupData.stype
      );

      if (groupIndex !== -1 && valueIndex !== -1) {
        updatedValues[groupIndex].values[valueIndex].quantity = updatedQuantity;

        // console.log(updatedValues);

        setStrikes(updatedValues);

        updateUrlParams({
          mode: "custom",
          symbol: selectedSymbol,
          strikes: JSON.stringify(updatedValues),
        });
      }
    }
  };

  // Function to remove a slider value
  const removeSliderValue = () => {
    if (currentGroupIndex === null || currentValueIndex === null) return; // Safety check

    // console.log('currentGroupIndex:',currentGroupIndex);
    // console.log('currentValueIndex:',currentValueIndex);

    // Create a copy of the current values
    const newValues = [...strikes];

    // Remove the value at the specified group and index
    newValues[currentGroupIndex].values.splice(currentValueIndex, 1);

    // Update the state with the new values

    setStrikes(newValues); // Update strikes if necessary

    // Create a copy of the current values
    const newValuesChange = [...strikesChange];

    // Remove the value at the specified group and index
    newValuesChange[currentGroupIndex].values.splice(currentValueIndex, 1);

    // Update the state with the new values
    setStrikesChange(newValuesChange); // Update strikes if necessary

    updateUrlParams({
      mode: "custom",
      symbol: selectedSymbol,
      strikes: JSON.stringify(newValues),
    });
    // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: selectedExpiry, strikes: JSON.stringify(newValues) });

    // Close the popup after removal
    closePopup();
  };

  // custom-slider close

  const textContentRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [isThumbDragging, setIsThumbDragging] = useState(false);

  const handleMouseDownslide = (e) => {
    // Prevent dragging the slider if a thumb is being dragged
    if (isThumbDragging) return;

    setIsDragging(true);
    const element = textContentRef.current;
    if (element) {
      setStartX(e.clientX - element.offsetLeft);
      element.style.cursor = "grabbing";
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const element = textContentRef.current;
      if (element) {
        const x = e.clientX - startX;
        const minx = -(screenWidth - 1200);
        if (x <= 0) {
          if (x < minx) {
            element.style.left = `${minx}px`;
          } else {
            element.style.left = `${x}px`;
          }
        } else {
          element.style.left = `0px`;
        }
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    const element = textContentRef.current;
    if (element) {
      element.style.cursor = "grab";
    }
  };

  React.useEffect(() => {
    const handleDocumentMouseUp = () => {
      if (isDragging) handleMouseUp();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleDocumentMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
    };
  }, [isDragging, startX]);

  /*************************ACTUAL Forecast CODE*********************************/

  
  //above code - expiries,allExpiries, stockPrice, strikes, selectedSymbol

  console.log('strikes:',strikes);
  console.log('strikes Beta:',strikesBeta);

  const getMaxExpiryDate = (expiries) => {
    const maxExpiryDate = new Date(
      Math.max(...expiries.map((date) => new Date(date).getTime()))
    );
    return maxExpiryDate;
  };

  const generateDates = (maxDate) => {
    const dates = [];
    const currentDate = new Date();
    const totalDays = Math.round((maxDate - currentDate) / (1000 * 3600 * 24));

    // console.log('totalDays:', totalDays);
    const interval = Math.ceil(totalDays / 20);

    for (let i = 0; i <= 20; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i * interval);

      if (date >= maxDate) {
        dates.push(maxDate.toISOString().split("T")[0]); // 🔥 Ensure last date is maxDate
        break;
      } else {
        dates.push(date.toISOString().split("T")[0]);
      }
    }

    return dates;
  };

  const [spValue, setSpValue] = useState(0);
  const spRef = useRef(0);
  const [ivValues, setIvValues] = useState({}); // Store IV values per expiry
  const [ppValues, setPpValues] = useState({}); // Store IV values per expiry

  const [data, setData] = useState({
    labels: [],
    datasets: [
      {
        label: "Implied volatility (%)",
        backgroundColor: "rgba(75,192,192,0.4)",
        borderColor: "rgba(75,192,192,1)",
        pointBackgroundColor: "rgba(75,192,192,1)",
        pointBorderColor: "#fff",
        data: [],
        pointHitRadius: 45, // Increase hit radius for easier selection
        yAxisID: "y-axis-0",
        hidden: true,
      },
      {
        label: "Price (%)",
        backgroundColor: "rgba(192,75,75,0.4)",
        borderColor: "rgba(192,75,75,1)",
        pointBackgroundColor: "rgba(192,75,75,1)",
        pointBorderColor: "#fff",
        data: [],
        pointHitRadius: 45, // Increase hit radius for easier selection
        yAxisID: "y-axis-1",
      },
      {
        label: "Payoff",
        backgroundColor: "rgba(75,75,192,0.4)",
        borderColor: "rgba(75,75,192,1)",
        pointBackgroundColor: "rgba(75,75,192,1)",
        pointBorderColor: "#fff",
        data: [],
        pointHitRadius: 15, // Increase hit radius for easier selection
        yAxisID: "y-axis-2",
        dragData: false, // Make the Payoff non-draggable
      },
      {
        label: "Payoff Beta", // New dataset for differenceDatasetBeta
        backgroundColor: "rgba(255,165,0,0.4)",
        borderColor: "rgba(255,165,0,1)",
        pointBackgroundColor: "rgba(255,165,0,1)",
        pointBorderColor: "#fff",
        data: [],
        pointHitRadius: 15,
        yAxisID: "y-axis-3",
        dragData: false,
        hidden: true,
      },
    ],
  });

  useEffect(() => {
    setData((prevData) => ({
      ...prevData,
      datasets: prevData.datasets.map((dataset) =>
        dataset.label === "Payoff Beta"
          ? { ...dataset, hidden: selectedSymbol !== "QQQ" }
          : dataset
      ),
    }));
  }, [selectedSymbol]);

  useEffect(() => {
    if (expiries.length > 0) {
      const maxExpiryDate = getMaxExpiryDate(expiries);
      const labels = generateDates(maxExpiryDate);
      //  const impliedVolatilityData = Array(labels.length).fill(20);
      const priceData = Array(labels.length).fill(5);

      // Initialize IV values per expiry
      const initialIVValues = {};
      labels.forEach((label) => {
        initialIVValues[label] = 20; // Default IV value
      });

      setIvValues(initialIVValues);

      const initialPPValues = {};
      labels.forEach((label) => {
        initialPPValues[label] = 5; // Default IV value
      });

      setPpValues(initialPPValues);

      setData((prevData) => ({
        labels: labels,
        datasets: prevData.datasets.map((dataset, index) => {
          if (index === 0) {
            return { ...dataset, data: Object.values(initialIVValues) };
          } else if (index === 1) {
            return { ...dataset, data: Object.values(initialPPValues) };
          } else {
            return dataset;
          }
        }),
      }));
    }
  }, [expiries, stockPrice, strikes, selectedSymbol]);

  // Sync input values when the graph updates
  useEffect(() => {
    if (data.labels.length > 0) {
      const newIvValues = {};
      data.labels.forEach((date, index) => {
        newIvValues[date] = data.datasets[0].data[index]; // Implied Volatility dataset
      });
      setIvValues(newIvValues);
    }
  }, [data.datasets[0].data, data.labels]); // 🔥 Update when IV changes in the graph

  // Sync input values when the graph updates
  useEffect(() => {
    if (data.labels.length > 0) {
      const newPpValues = {};
      data.labels.forEach((date, index) => {
        newPpValues[date] = data.datasets[1].data[index]; // Implied Volatility dataset
      });
      // console.log('newPpValues:',newPpValues);
      setPpValues(newPpValues);
    }
  }, [data.datasets[1].data, data.labels]); // 🔥 Update when IV changes in the graph

  const updateDatabase = async (newData) => {
    // console.log(newData);

    await fetch(`${API_URL}update_options_values_data.php?v=1`, {
      method: "POST",
      // mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newData),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        return res.text(); // First, read raw text response
      })
      .then((text) => {
        try {
          const json = JSON.parse(text); // Try parsing JSON
          // console.log("Data saved:", json);
        } catch (err) {
          console.error("Invalid JSON response from server:", text);
        }
      })
      .catch((error) => console.error("Error saving data:", error));
  };

  // Handle input change
  const handleIVChange = (date, value) => {
    const newIv = parseFloat(value) || 0;

    // Update input value
    setIvValues((prev) => ({ ...prev, [date]: newIv }));

    const updatedData = {
      [date]: {
        iv_value: newIv,
        pp_value: ppValues[date],
        expiryDate: date,
        symbol: selectedSymbol,
      },
    };
    updateDatabase(updatedData);

    // Update the graph data
    setData((prevData) => ({
      ...prevData,
      datasets: prevData.datasets.map((dataset, index) => {
        if (index === 0) {
          // Implied Volatility dataset
          return {
            ...dataset,
            data: prevData.labels.map((d, i) =>
              d === date ? newIv : dataset.data[i]
            ),
          };
        }
        return dataset;
      }),
    }));
  };

  // Handle input change
  const handlePPChange = (date, value) => {
    const newPp = parseFloat(value) || 0;

    // Update input value
    setPpValues((prev) => ({ ...prev, [date]: newPp }));

    const updatedData = {
      [date]: {
        iv_value: ivValues[date],
        pp_value: newPp,
        expiryDate: date,
        symbol: selectedSymbol,
      },
    };
    // console.log('updatedData:',updatedData);
    updateDatabase(updatedData);

    // // Update the graph data
    setData((prevData) => ({
      ...prevData,
      datasets: prevData.datasets.map((dataset, index) => {
        if (index === 1) {
          // Implied Volatility dataset
          return {
            ...dataset,
            data: prevData.labels.map((d, i) =>
              d === date ? newPp : dataset.data[i]
            ),
          };
        }
        return dataset;
      }),
    }));
  };

  useEffect(() => {
    if (data.labels.length > 0) {
      const { datasets, labels } = data;
      const impliedVolatilityData = datasets[0].data;
      const priceData = datasets[1].data;
      const differenceDataset = [];
      const differenceDatasetBeta = [];

      let lastSP = spRef.current; // Store the last calculated SP value

      let spMap = {};

      let flatStrikes = strikes.flatMap((strike) =>
        Array.isArray(strike.values)
          ? strike.values.map((val) => ({ ...val }))
          : []
      );

      let flatStrikesBeta = strikesBeta.flatMap((strike) =>
        Array.isArray(strike.values)
          ? strike.values.map((val) => ({ ...val }))
          : []
      );

      for (let i = 0; i < labels.length; i++) {
        const expiryDate = labels[i];
        const dateDifference =
          new Date(labels[labels.length - 1]).getTime() -
          new Date(labels[i]).getTime();
        const dateDifferenceInDays = dateDifference / (1000 * 3600 * 24);

        // console.log('dateDifference:',dateDifference);

        let SP = stockPrice + (stockPrice * priceData[i]) / 100;
        lastSP = SP; // Store latest SP

        spMap[expiryDate] = SP; // Store in map

        let T = dateDifferenceInDays / 365;
        const riskFreeRate = 0.0379;
        let strike_iv = impliedVolatilityData[i] / 100;

        // console.log(`stockPrice: ${SP}, priceData: ${priceData[i]}, T: ${T}, strike_iv: ${strike_iv}`);

        let payoff = 0;
        let all_optionValue1 = 0,
          all_optionValue2 = 0,
          all_optionValue3 = 0,
          all_optionValue4 = 0;

        flatStrikes.forEach((strike) => {
          if (strike.symbol === selectedSymbol) {
            const lotMultiplier = strike.quantity ?? 1;
            const avg_price_share = strike.avg_price_share;
            const shares = lotMultiplier * contractSize;
            const strike_value = strike.value ?? 0;

            if (strike.type === "Buy Call") {
              const all_bs1 = calculateBlackScholes(
                SP,
                strike_value,
                T,
                riskFreeRate,
                strike_iv,
                "call"
              );
              all_optionValue1 += shares * (all_bs1 - avg_price_share);
            } else if (strike.type === "Buy Put") {
              const all_bs2 = calculateBlackScholes(
                SP,
                strike_value,
                T,
                riskFreeRate,
                strike_iv,
                "put"
              );
              all_optionValue2 += shares * (all_bs2 - avg_price_share);
            } else if (strike.type === "Sell Call") {
              const all_bs3 = calculateBlackScholes(
                SP,
                strike_value,
                T,
                riskFreeRate,
                strike_iv,
                "call"
              );
              all_optionValue3 += shares * (all_bs3 - avg_price_share);
            } else if (strike.type === "Sell Put") {
              const all_bs4 = calculateBlackScholes(
                SP,
                strike_value,
                T,
                riskFreeRate,
                strike_iv,
                "put"
              );
              all_optionValue4 += shares * (all_bs4 - avg_price_share);
            }
          }
        });

        payoff =
          all_optionValue1 +
          all_optionValue2 -
          all_optionValue3 -
          all_optionValue4;
        // console.log('payoff:', payoff);
        differenceDataset.push(payoff.toFixed(2));

        let payoffBeta = 0;
        let all_optionValue1Beta = 0,
          all_optionValue2Beta = 0,
          all_optionValue3Beta = 0,
          all_optionValue4Beta = 0;

        flatStrikesBeta.forEach((strike) => {
            const lotMultiplier = strike.quantity ?? 1;
            const avg_price_share = strike.avg_price_share;
            const beta = strike.beta;
            const shares = lotMultiplier * contractSize;
            const strike_value = strike.value ?? 0;

            if (strike.type === "Buy Call") {
              const all_bs1 = calculateBlackScholes(
                SP,
                strike_value,
                T,
                riskFreeRate,
                strike_iv,
                "call"
              );
              all_optionValue1Beta += shares * beta *(all_bs1 - avg_price_share);
            } else if (strike.type === "Buy Put") {
              const all_bs2 = calculateBlackScholes(
                SP,
                strike_value,
                T,
                riskFreeRate,
                strike_iv,
                "put"
              );
              all_optionValue2Beta += shares * beta *(all_bs2 - avg_price_share);
            } else if (strike.type === "Sell Call") {
              const all_bs3 = calculateBlackScholes(
                SP,
                strike_value,
                T,
                riskFreeRate,
                strike_iv,
                "call"
              );
              all_optionValue3Beta += shares * beta *(all_bs3 - avg_price_share);
            } else if (strike.type === "Sell Put") {
              const all_bs4 = calculateBlackScholes(
                SP,
                strike_value,
                T,
                riskFreeRate,
                strike_iv,
                "put"
              );
              all_optionValue4Beta += shares * beta *(all_bs4 - avg_price_share);
            }
          
        });

        payoffBeta =
          all_optionValue1Beta +
          all_optionValue2Beta -
          all_optionValue3Beta -
          all_optionValue4Beta;
        console.log('payoffBeta:', payoffBeta);
        differenceDatasetBeta.push(payoffBeta.toFixed(2));
      }

      // 🔥 Update `spValue` once after processing all data
      spRef.current = lastSP;
      setSpValue(spMap); // Triggers re-render

      setData((prevData) => ({
        ...prevData,
        datasets: prevData.datasets.map((dataset, index) => {
          if (index === 2) {
            return { ...dataset, data: differenceDataset };
          } else if (index === 3) {
            // return { ...dataset, data: differenceDatasetBeta };
            return selectedSymbol === "QQQ"
            ? { ...dataset, data: differenceDatasetBeta }
            : { ...dataset, data: [] }; // Hide if not QQQ
          }
          return dataset;
        }),
      }));
    }
  }, [data.labels, data.datasets[0].data, data.datasets[1].data]);

  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (data.labels.length > 0) {
      const { datasets, labels } = data;
      const impliedVolatilityData = datasets[0].data;
      const priceData = datasets[1].data;
      const payoffData = datasets[2].data;

      const newTableData = labels.map((date, index) => {
        const dateDifference =
          new Date(labels[labels.length - 1]).getTime() -
          new Date(labels[index]).getTime();
        const T = dateDifference / (1000 * 3600 * 24) / 365;

        const priceValue = parseFloat(priceData[index]) || 0; // Convert to number, default to 0
        const ivValue = parseFloat(impliedVolatilityData[index]) || 0; // Convert IV to number, default to 0

        return {
          expiryDate: date,
          stockPrice: (stockPrice + (stockPrice * priceValue) / 100).toFixed(2),
          priceData: priceValue.toFixed(2),
          strikeIV: ivValue.toFixed(2),
          T: T.toFixed(4),
          payoff: payoffData[index] ?? 0, // Ensure payoff has a default value
        };
      });

      setTableData(newTableData);
    }
  }, [data, stockPrice]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${API_URL}update_options_values_data.php?v=2`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        // console.log("Fetched Data:", data);

        if (!data || Object.keys(data).length === 0) {
          console.warn("⚠ API returned empty data!");
          return; // Stop execution if no data
        }

        // Update ivValues only if expiry exists, otherwise keep the original value
        setIvValues((prevIvValues) => {
          return Object.fromEntries(
            Object.entries(prevIvValues).map(([expiry, oldVal]) => [
              expiry,
              data[expiry] !== undefined &&
              data[expiry].symbol === selectedSymbol
                ? parseFloat(data[expiry].iv_value) || oldVal
                : oldVal, // Keep old value if no update
            ])
          );
        });

        // Update ppValues only if expiry exists, otherwise keep the original value
        setPpValues((prevPpValues) => {
          return Object.fromEntries(
            Object.entries(prevPpValues).map(([expiry, oldVal]) => [
              expiry,
              data[expiry] !== undefined &&
              data[expiry].symbol === selectedSymbol
                ? parseFloat(data[expiry].pp_value) || oldVal
                : oldVal, // Keep old value if no update
            ])
          );
        });

        // Update datasets only if expiry exists
        setData((prevData) => ({
          ...prevData,
          datasets: prevData.datasets.map((dataset, index) => {
            if (index === 0) {
              return {
                ...dataset,
                data: prevData.labels.map(
                  (expiry, i) =>
                    data[expiry] !== undefined &&
                    data[expiry].symbol === selectedSymbol
                      ? parseFloat(data[expiry].iv_value)
                      : dataset.data[i] ?? prevData.datasets[index].data[i] // Keep old value
                ),
              };
            } else if (index === 1) {
              return {
                ...dataset,
                data: prevData.labels.map(
                  (expiry, i) =>
                    data[expiry] !== undefined &&
                    data[expiry].symbol === selectedSymbol
                      ? parseFloat(data[expiry].pp_value)
                      : dataset.data[i] ?? prevData.datasets[index].data[i] // Keep old value
                ),
              };
            } else {
              return dataset;
            }
          }),
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [expiries, stockPrice, strikes, selectedSymbol]);

  // console.log('ivValues:',ivValues);

  const exportToExcel = (tableData) => {
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Options Data");

    // Save the file
    XLSX.writeFile(workbook, "Options_Forecast_Data.xlsx");
  };

  //    console.log('data:',data);

  const differenceValues0 = data.datasets[0].data.filter(
    (value) => value !== null
  );
  const differenceValues1 = data.datasets[1].data.filter(
    (value) => value !== null
  );
  const differenceValues2 = data.datasets[2].data.filter(
    (value) => value !== null
  );
  const differenceValues3 = data.datasets[3].data.filter(
    (value) => value !== null
  );

  const allDifferenceValues = [
    ...differenceValues0,
    ...differenceValues1,
    ...differenceValues2,
  ];
  const minDifference = Math.min(...allDifferenceValues);
  const maxDifference = Math.max(...allDifferenceValues);

  const minDifferenceBeta = Math.min(...differenceValues3);
  const maxDifferenceBeta = Math.max(...differenceValues3);

  const handleGraphDrag = (datasetIndex, index, value) => {
    const date = data.labels[index]; // Get expiry date

    if (datasetIndex === 0) {
      // Dragged IV Value
      setIvValues((prev) => ({ ...prev, [date]: value }));
      updateDatabase({
        [date]: {
          iv_value: value,
          pp_value: ppValues[date],
          expiryDate: date,
          symbol: selectedSymbol,
        },
      });
    } else if (datasetIndex === 1) {
      // Dragged PP Value
      setPpValues((prev) => ({ ...prev, [date]: value }));
      updateDatabase({
        [date]: {
          iv_value: ivValues[date],
          pp_value: value,
          expiryDate: date,
          symbol: selectedSymbol,
        },
      });
    }

    // Update Graph Data
    setData((prevData) => ({
      ...prevData,
      datasets: prevData.datasets.map((dataset, i) => {
        if (i === datasetIndex) {
          return {
            ...dataset,
            data: prevData.labels.map((d, idx) =>
              idx === index ? value : dataset.data[idx]
            ),
          };
        }
        return dataset;
      }),
    }));
  };

  const options = {
    responsive: true,
    scales: {
      x: {
        type: "category",
        grid: {
          color: "#363636",
        },
      },
      "y-axis-0": {
        beginAtZero: true,
        min: 0,
        max: 100,
        position: "right",
        grid: {
          color: "#363636",
        },
      },
      "y-axis-1": {
        beginAtZero: true,
        min: -100,
        max: 150,
        position: "right",
        grid: {
          color: "#363636",
        },
      },
      "y-axis-2": {
        beginAtZero: true,
        min: minDifference - 50,
        max: maxDifference + 50,
        position: "left",
        grid: {
          drawOnChartArea: false,
          color: "#363636",
        },
      },
      "y-axis-3": {
        beginAtZero: true,
        min: minDifferenceBeta - 50,
        max: maxDifferenceBeta + 50,
        position: "left",
        grid: {
          drawOnChartArea: false,
          color: "#363636",
        },
      },
    },
    plugins: {
      legend: {
        display: true,
      },
      tooltip: {
        callbacks: {
          beforeLabel: function (context) {
            const expiryDate = context.label; // 🔥 Get expiry date
            const currentSP = spValue[expiryDate] ?? 0; // 🔥 Fetch SP
            return `Price: ${currentSP.toFixed(2)}`;
          },
          label: function (context) {
            const dataIndex = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            const dataset = context.chart.data.datasets[datasetIndex];
            return `${dataset.label}: ${dataset.data[dataIndex]}`;
          },
          afterLabel: function (context) {
            const dataIndex = context.dataIndex;
            const datasets = context.chart.data.datasets;
            const result = [];
            datasets.forEach((dataset, index) => {
              if (index !== context.datasetIndex) {
                result.push(`${dataset.label}: ${dataset.data[dataIndex]}`);
              }
            });
            return result;
          },
        },
        displayColors: false, // ✅ Clean tooltip look
        bodyFont: { size: 14 },
      },
      dragData: {
        round: 1,
        showTooltip: true,
        onDragStart: (e, datasetIndex) => {
          if (datasetIndex === 2) return;
          e.target.style.cursor = "grabbing";
        },
        onDragEnd: (e, datasetIndex, index, value) => {
          handleGraphDrag(datasetIndex, index, value);

          if (datasetIndex === 2) return;
          e.target.style.cursor = "default";
          const newData = data.datasets[datasetIndex].data.map((d, i) =>
            i === index ? value : d
          );
          setData((prevData) => ({
            ...prevData,
            datasets: prevData.datasets.map((dataset, i) => {
              if (i === datasetIndex) {
                return { ...dataset, data: newData };
              }
              return dataset;
            }),
          }));
        },
      },
    },
  };

  const chartRef = useRef(null);

  return (
    <div style={{ width: "96%", margin: "auto" }}>
      <Modal show={show} onHide={() => setShow(true)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Login</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleLogin}>
            <Form.Group controlId="formUsername" className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formPassword" className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formRememberMe" className="mb-3">
              <Form.Check
                type="checkbox"
                label="Remember Me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
            </Form.Group>

            {error && <p className="text-danger">{error}</p>}

            <Button variant="primary" type="submit" className="w-100">
              Login
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <h2>Options Forecast Chart</h2>

      <div className="ds">
        <div className="ds2">
          {stockPrice ? (
            <div className="UnderlyingDetails_price__left__xitcX">
              <span style={{ fontWeight: 500 }}>{selectedSymbol}</span> Spot
              price <span style={{ fontWeight: 500 }}>${stockPrice}</span>{" "}
            </div>
          ) : (
            ""
          )}

          <Dropdown
            onSelect={handleacct_nameChange}
            style={{ display: hideACCName == "block" ? "block" : "none" }}
          >
            <Dropdown.Toggle variant="success" id="acct_name-dropdown">
              {selectedacct_name ? selectedacct_name : "Select Account Name"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                key="All Accounts"
                eventKey="All Accounts"
                style={{}}
              >
                All Accounts
              </Dropdown.Item>
              {acct_names.map((acct_name, index) => (
                <Dropdown.Item key={index} eventKey={acct_name}>
                  {acct_name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown onSelect={handleSymbolChange}>
            <Dropdown.Toggle variant="success" id="symbol-dropdown">
              {selectedSymbol ? selectedSymbol : "Select Stock"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {symbols.map((symbol, index) => (
                <Dropdown.Item key={index} eventKey={symbol}>
                  {symbol}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {loading && (
        <div className="loader">
          <RotatingLines
            strokeColor="grey"
            strokeWidth="5"
            animationDuration="0.75"
            width="100"
            visible={true}
          />
        </div>
      )}

      {/* custom-slider */}

      <div className="sliderOver" style={{ padding: `${sliderOverPad}px 0` }}>
        <div className="slider">
          <div id="draggable">
            <div
              id="text-content"
              ref={textContentRef}
              onMouseDown={handleMouseDownslide}
              style={{ position: "relative" }}
              // style={{ position: "relative", left: `${textContentRefLeft}px` }}
            >
              <div
                className="slider-container"
                ref={sliderRef}
                style={{ width: `${screenWidth}px` }}
              >
                <div className="range-slider">
                  {
                    // Ensure min and max are valid numbers
                    min != null &&
                    max != null &&
                    !isNaN(min) &&
                    !isNaN(max) &&
                    Math.round(max) > Math.round(min) ? (
                      Array.from({
                        length: Math.round(max) - Math.round(min) + 1,
                      }).map((_, index) => {
                        const value = Math.round(min) + index; // Ensure value is an integer
                        const percent =
                          ((value - Math.round(min)) /
                            (Math.round(max) - Math.round(min))) *
                          100; // Position in percentage
                        const isMultipleOf5 = value % 5 === 0; // Check for multiples of 5
                        const isMultipleOf10 = value % 10 === 0; // Check for multiples of 10

                        return (
                          <div
                            key={value}
                            className="slider-label"
                            style={{
                              position: "absolute",
                              left: `${percent}%`,
                              transform: "translateX(-50%)",
                              top: isMultipleOf10
                                ? "-25px"
                                : isMultipleOf5
                                ? "-20px"
                                : "-15px",
                              fontSize: isMultipleOf10
                                ? "12px"
                                : isMultipleOf5
                                ? "10px"
                                : "8px",
                              fontWeight: isMultipleOf10 ? "bold" : "normal",
                            }}
                          >
                            {isMultipleOf5
                              ? isMultipleOf10
                                ? value
                                : value
                              : "|"}
                          </div>
                        );
                      })
                    ) : (
                      <div>Loading...</div> // Fallback message when min and max are invalid
                    )
                  }
                </div>

                {minStrikePrice > 0 &&
                  strikes?.length > 0 &&
                  strikes.map(
                    (group, groupIndex) =>
                      group.values?.length > 0 &&
                      group.values.map((item, valueIndex) => {
                        if (item.value <= 0) return null; // Skip rendering for invalid values

                        return (
                          <div
                            key={`${groupIndex}-${valueIndex}`}
                            className={`thumb thumb-${groupIndex}-${valueIndex} ${
                              item.stype === "C" ? "green-thumb" : "red-thumb"
                            }`}
                            onMouseDown={handleMouseDown(
                              groupIndex,
                              valueIndex
                            )}
                            onContextMenu={(event) =>
                              handleContextMenucus(
                                event,
                                groupIndex,
                                valueIndex
                              )
                            }
                            onClick={closePopup}
                          >
                            <div
                              className={`value-block ${
                                group.type === "Buy"
                                  ? "above-slider"
                                  : "below-slider"
                              }`}
                            >
                              {item.value}
                              <span className="strike-below">
                                {item.stype}
                                {item.avg_price_share_new === 0 ? (
                                  <span className="StrikeSelector_point__icon__km_Ta strike_icon">
                                    <svg
                                      aria-hidden="true"
                                      focusable="false"
                                      data-prefix="fas"
                                      data-icon="triangle-exclamation"
                                      className="svg-inline--fa fa-triangle-exclamation ItemIcons_icon__WDozd ItemIcons_icon--warning__TP2Sz ItemIcons_icon--shadow__6Vgg2"
                                      role="img"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 512 512"
                                    >
                                      <path
                                        fill="currentColor"
                                        d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480L40 480c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24l0 112c0 13.3 10.7 24 24 24s24-10.7 24-24l0-112c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"
                                      ></path>
                                    </svg>
                                  </span>
                                ) : (
                                  ""
                                )}
                              </span>
                              <span className="strike-qty" style={{}}>
                                x{item.quantity}
                              </span>
                            </div>
                          </div>
                        );
                      })
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup */}
      {popupData && (
        <div
          className="popup"
          style={{ left: `${popupData.x}px`, top: `${popupData.y}px` }}
        >
          <div className="ContextMenu_container__Gv9l9 context-menu" style={{}}>
            <div className="ContextMenu_row__A_hbc ContextMenu_row--header__Ck4t9">
              {selectedSymbol}{" "}
              <span
                style={{
                  color: `${popupData.stype === "C" ? "#00c038" : "#c20028"}`,
                }}
              >
                {popupData.value}
                {popupData.stype}
              </span>{" "}
              <span style={{ color: "yellow" }}>{popupData.expiry}</span>
            </div>

            {popupData.avg_price_share_new === 0 ? (
              <div className="ContextMenu_row__A_hbc">
                <span className="OptionDetailsWarnings_warnings___6mR7">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="far"
                    data-icon="triangle-exclamation"
                    className="svg-inline--fa fa-triangle-exclamation "
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path
                      fill="currentColor"
                      d="M248.4 84.3c1.6-2.7 4.5-4.3 7.6-4.3s6 1.6 7.6 4.3L461.9 410c1.4 2.3 2.1 4.9 2.1 7.5c0 8-6.5 14.5-14.5 14.5l-387 0c-8 0-14.5-6.5-14.5-14.5c0-2.7 .7-5.3 2.1-7.5L248.4 84.3zm-41-25L9.1 385c-6 9.8-9.1 21-9.1 32.5C0 452 28 480 62.5 480l387 0c34.5 0 62.5-28 62.5-62.5c0-11.5-3.2-22.7-9.1-32.5L304.6 59.3C294.3 42.4 275.9 32 256 32s-38.3 10.4-48.6 27.3zM288 368a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm-8-184c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 96c0 13.3 10.7 24 24 24s24-10.7 24-24l0-96z"
                    ></path>
                  </svg>{" "}
                  Please change the strike price, this stike price is not
                  available on this expiry.
                </span>
              </div>
            ) : (
              ""
            )}

            <div className="ContextMenu_row__A_hbc ContextMenu_row--columns__Damcu">
              {/* Quantity and Price Sections */}
              <div className="ContextMenu_column__8ueQs">
                <div>Quantity</div>
                <div className="PriceAndQuantityMenu_control__BPi17">
                  <span
                    className="icon-button PriceAndQuantityMenu_qty__icon__lbCxZ"
                    role="button"
                    tabIndex="0"
                    onClick={() => handleQuantityChangepop(-1)} // Decrease quantity
                  >
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      data-prefix="fas"
                      data-icon="circle-chevron-left"
                      className="svg-inline--fa fa-circle-chevron-left"
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                    >
                      <path
                        fill="currentColor"
                        d="M512 256A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM271 135c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-87 87 87 87c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L167 273c-9.4-9.4-9.4-24.6 0-33.9L271 135z"
                      />
                    </svg>
                  </span>
                  <strong className="PriceAndQuantityMenu_qty__value__xB0h1">
                    <span
                      data-editable="true"
                      tabIndex="0"
                      role="button"
                      aria-label="Edit"
                      className="EditableText_host__RKtXH"
                    >
                      <span className="EditableText_text__7BEoU" tabIndex="0">
                        {popupData.quantity}
                      </span>
                    </span>
                  </strong>
                  <span
                    className="icon-button PriceAndQuantityMenu_qty__icon__lbCxZ"
                    role="button"
                    tabIndex="0"
                    onClick={() => handleQuantityChangepop(1)} // Increase quantity
                  >
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      data-prefix="fas"
                      data-icon="circle-chevron-right"
                      className="svg-inline--fa fa-circle-chevron-right"
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                    >
                      <path
                        fill="currentColor"
                        d="M0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zM241 377c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l87-87-87-87c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L345 239c9.4 9.4 9.4 24.6 0 33.9L241 377z"
                      />
                    </svg>
                  </span>
                </div>
              </div>

              <div className="ContextMenu_column__8ueQs">
                <div>Price</div>
                <div className="PriceAndQuantityMenu_control__BPi17">
                  <strong>
                    $
                    <span
                      data-editable="true"
                      tabIndex="0"
                      role="button"
                      aria-label="Edit"
                      className="EditableText_host__RKtXH"
                    >
                      <span className="EditableText_text__7BEoU" tabIndex="0">
                        {thumbMoved
                          ? popupData.avg_price_share_new
                          : popupData.avg_price_share}
                      </span>
                    </span>
                  </strong>
                </div>
              </div>
            </div>
            <div className="ContextMenu_row__A_hbc OptionDetails_greeks__iIe26">
              {[
                { label: "Bid", value: bidprice, color: "green" },
                { label: "Ask", value: askprice, color: "red" },
                { label: "Volume", value: volume },
                { label: "OI", value: oi },
                { label: "IV", value: iv.toFixed(2) + "%" },
                { label: "Delta", value: delta },
                { label: "Theta", value: theta },
                { label: "Gamma", value: gamma },
                { label: "Vega", value: vega },
                { label: "Rho", value: rho },
              ].map((greek, index) => (
                <div className="OptionDetails_greek__IMSKG" key={index}>
                  <span>{greek.label} </span>
                  <span
                    className={`OptionDetails_greek__value__WLRnm ${
                      greek.color || ""
                    }`}
                  >
                    {greek.value}
                  </span>
                </div>
              ))}
            </div>
            {/* Remove Button */}

            <div className="ContextMenu_row__A_hbc">
              <label className="expiry-select" htmlFor="expiry-select">
                Change Expiration
              </label>
              <select
                id="expiry-select"
                value={popupData.expiry || ""}
                onChange={(e) => handleExpiryChangePopup(e.target.value)}
                className="expiry-dropdown"
              >
                {allExpiries.map((expiry, idx) => (
                  <option key={idx} value={expiry}>
                    {expiry}
                  </option>
                ))}
              </select>
            </div>

            <div
              onClick={removeSliderValue}
              className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO"
              role="button"
              tabIndex="0"
            >
              <svg
                aria-hidden="true"
                focusable="false"
                data-prefix="far"
                data-icon="xmark"
                className="svg-inline--fa fa-xmark"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 384 512"
                style={{ marginRight: "5px" }}
              >
                <path
                  fill="currentColor"
                  d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"
                ></path>
              </svg>
              Remove{" "}
            </div>
            <div
              onClick={closePopup}
              className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO"
              role="button"
              tabIndex="0"
            >
              Close{" "}
            </div>
          </div>
        </div>
      )}

{/* **************************************************CHART************************************************* */}
      <Line ref={chartRef} data={data} options={options} />

      <div
        style={{
          display: "flex",
          overflowX: "auto",
          gap: "3px",
          alignItems: "center",
          padding: "5px 0",
        }}
      >
        {data.labels.map((date) => (
          <div key={date} style={{ textAlign: "center" }}>
            <label style={{ display: "block", fontSize: "11px" }}>{date}</label>
            <input
              className="mb-2"
              min="0"
              max="100"
              type="number"
              value={ivValues[date] || ""}
              onChange={(e) => handleIVChange(date, e.target.value)}
              style={{ width: "50px", fontSize: "12px", textAlign: "center" }}
            />
            <input
              min="-100"
              max="150"
              type="number"
              value={ppValues[date] || ""}
              onChange={(e) => handlePPChange(date, e.target.value)}
              style={{ width: "50px", fontSize: "12px", textAlign: "center" }}
            />
          </div>
        ))}
      </div>
      <div style={{ height: "20px" }}></div>
      <div>
        <h3>Options Forecast Data Table</h3>
        <button
          onClick={() => exportToExcel(tableData)}
          className=" btn btn-success mb-3"
        >
          Export to Excel
        </button>
        <table
          border="1"
          cellPadding="5"
          cellSpacing="0"
          className="table text-center"
        >
          <thead>
            <tr>
              <th>Expiry Date</th>
              <th>Stock Price</th>
              <th>Price(%)</th>
              <th>Strike IV(%)</th>
              <th>T (in years)</th>
              <th>Payoff</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => (
              <tr key={index}>
                <td>{row.expiryDate}</td>
                <td>{row.stockPrice}</td>
                <td>{row.priceData}</td>
                <td>{row.strikeIV}</td>
                <td>{row.T}</td>
                <td>{row.payoff}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ height: "20px" }}></div>
    </div>
  );
};

export default OptionsGraphForecast;
